import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { clinicAPI } from '../services/api';
import { apiManager } from '../services/apiManager';
import clinicService, { CLINIC_SETUP_DATA_ENDPOINT } from '../services/clinicService';

/**
 * Custom hook to fetch and manage clinic setup data
 * This centralizes all data loading for clinic information components
 */
export const useClinicData = () => {
  // Get clinicId from URL params first, fallback to localStorage
  const params = useParams();
  const urlClinicId = params?.clinicId;
  
  const [data, setData] = useState(clinicService.getSharedData());
  const [loading, setLoading] = useState(clinicService.getSharedLoading());
  const [error, setError] = useState(clinicService.getSharedError());
  const [refreshCounter, setRefreshCounter] = useState(clinicService.getRefreshCounter());

  // Listen for refresh events from other instances
  useEffect(() => {
    const handleRefresh = () => {
      // Update to the latest shared counter value
      const currentCounter = clinicService.getRefreshCounter();
      if (currentCounter > refreshCounter) {
        setRefreshCounter(currentCounter);
      }
    };
    
    window.addEventListener('clinicDataRefresh', handleRefresh);
    return () => {
      window.removeEventListener('clinicDataRefresh', handleRefresh);
    };
  }, [refreshCounter]);

  useEffect(() => {
    // Get clinic_id from URL params only - no localStorage fallback
    const clinicId = urlClinicId;
    
    // Create endpoint key that includes clinic_id for proper caching
    const endpointKey = clinicService.createEndpointKey(CLINIC_SETUP_DATA_ENDPOINT, clinicId);
    
    // Get shared data from service
    const sharedData = clinicService.getSharedData();
    
    // If we already have data and it matches the current clinic_id, use it
    // Note: resetClinicData() (called by refresh()) already clears sharedData, so
    // when refreshCounter changes, sharedData will be null and fresh data will be fetched
    if (sharedData && clinicService.dataMatchesClinicId(sharedData, clinicId)) {
      setData(sharedData);
      setLoading(false);
      setError(null);
      return;
    }
    
    // If clinic_id changed, clear shared data to fetch fresh data
    clinicService.clearDataIfClinicChanged(clinicId);

    // If there's already a call in progress, wait for it
    const dataPromise = clinicService.getDataPromise();
    if (dataPromise) {
      dataPromise.then((result) => {
        setData(result.data);
        setLoading(false);
        setError(result.error);
      }).catch((err) => {
        setError(err.message || 'Failed to load clinic data');
        setLoading(false);
      });
      return;
    }

    // Check if API call is already in progress to prevent duplicate calls
    // But allow fetch if we have no data (data was reset or cleared)
    const isInProgress = apiManager.isCallInProgress(endpointKey);
    if (isInProgress && sharedData !== null) {
      // Only skip if call is in progress AND we have data
      // If data is null, we need to fetch even if endpoint was previously completed
      return;
    }
    
    // If endpoint was completed but data is null, reset it to allow refetch
    if (isInProgress && sharedData === null) {
      apiManager.resetCall(endpointKey);
    }

    const fetchClinicData = async () => {
      apiManager.startCall(endpointKey);
      
      try {
        setLoading(true);
        setError(null);
        clinicService.setSharedLoading(true);
        clinicService.setSharedError(null);
        
        // Send clinic_id as query parameter
        const params = clinicId ? { clinic_id: clinicId } : {};
        
        // Use clinicAPI instead of direct api.get
        const response = await clinicAPI.getClinicSetupData(params);
        
        // Transform and validate response using service
        const responseData = clinicService.transformClinicSetupResponse(response);
        
        setData(responseData);
        clinicService.setSharedData(responseData);
        
        // Return result object for promise chaining
        return { data: responseData, error: null };
      } catch (err) {
        console.error('[useClinicData] Error fetching clinic data:', err);
        const errorMessage = err.message || 'Failed to load clinic data';
        setError(errorMessage);
        clinicService.setSharedError(errorMessage);
        
        // Return error for promise chaining
        return { data: null, error: errorMessage };
      } finally {
        setLoading(false);
        clinicService.setSharedLoading(false);
        apiManager.completeCall(endpointKey);
        clinicService.setDataPromise(null);
      }
    };

    // Create a promise that other instances can wait for
    const promise = fetchClinicData();
    clinicService.setDataPromise(promise);
  }, [refreshCounter, urlClinicId]); // Re-run when refresh counter or URL clinicId changes 

  // Use clinicService methods for data extraction
  const getClinicInfo = () => clinicService.getClinicInfo(data);
  const getClinicTypes = () => clinicService.getClinicTypes(data);
  const getCountries = () => clinicService.getCountries(data);
  const getClinicServices = () => clinicService.getClinicServices(data);
  const getUserLocation = () => clinicService.getUserLocation(data);
  const getClinicMembers = () => clinicService.getClinicMembers(data);
  const getOwnedClinics = () => clinicService.getOwnedClinics(data);
  const getClinicId = () => {
    // Return clinicId from URL if available
    if (urlClinicId) return urlClinicId;

    // Fallback to resolved clinic data when URL has no clinicId
    // (e.g. moderators landing on backward-compatible routes)
    return data?.clinic?.id || data?.registered_clinic_id || null;
  };
  const isOwner = () => clinicService.isOwner(urlClinicId);
  const isMember = () => clinicService.isMember(data);

  // Refresh function to trigger re-fetch across all hook instances
  const refresh = () => {
    clinicService.resetClinicData();
    const newCounter = clinicService.incrementRefreshCounter();
    // Update local state to trigger re-render and re-fetch
    setRefreshCounter(newCounter);
  };

  return {
    data,
    loading,
    error,
    getClinicInfo,
    getClinicTypes,
    getCountries,
    getClinicServices,
    getUserLocation,
    getClinicMembers,
    getOwnedClinics,
    getClinicId,
    isOwner,
    isMember,
    clinic: data?.clinic,
    clinicTypes: data?.clinic_types,
    countries: data?.countries,
    clinicServices: data?.clinic_service_list,
    userLocation: data?.user_location,
    clinicMembers: data?.clinic_members,
    ownedClinics: data?.owned_clinics || [],
    refresh, // Export refresh function
  };
};

// Function to reset shared data (useful for logout or data refresh)
export const resetClinicData = () => {
  clinicService.resetClinicData();
};

export default useClinicData;
